import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1533664488202-6af66d26c44a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHx0ZWxlY29tJTIwdG93ZXIlMjBkdXNrfGVufDB8fHx8MTc4Mzc5NDY3MXww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { user, ready, login } = useAuth();
  const [email, setEmail] = useState("dasrajat131@gmail.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (ready && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0B0C10]">
      {/* Left - Hero */}
      <div className="hidden md:block relative overflow-hidden border-r border-white/10">
        <img src={HERO_IMG} alt="Telecom tower" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-[#FF5722]/20" />
        <div className="relative h-full flex flex-col justify-between p-10">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FF5722] flex items-center justify-center">
                <Building2 className="w-5 h-5 text-black" />
              </div>
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#FF5722]">FieldOps ERP</div>
                <div className="text-xl font-semibold">SIPL / Syncrova India</div>
              </div>
            </div>
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#FF5722] mb-3">Telecom Field Operations</div>
            <h2 className="text-4xl font-semibold leading-tight max-w-md">
              Tasks. Expenses.<br/>Attendance. Approvals.<br/>
              <span className="text-[#FF5722]">All in one system.</span>
            </h2>
            <p className="text-sm text-slate-300 mt-4 max-w-sm">
              A dedicated platform for engineers, managers, and HOD to run OLT installation & commissioning
              across all circles in India.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[
                { k: "CIRCLES", v: "8" },
                { k: "STATES", v: "36" },
                { k: "ENGINEERS", v: "18+" },
              ].map((s) => (
                <div key={s.k} className="border-l border-[#FF5722] pl-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-slate-400">{s.k}</div>
                  <div className="mono text-2xl font-medium">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right - Login form */}
      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-sm">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#FF5722] mb-3">Portal Access</div>
          <h1 className="text-3xl font-semibold mb-1">Sign in</h1>
          <p className="text-sm text-slate-400 mb-8">
            Enter your credentials to access the FieldOps portal.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0B0C10] border border-white/15 focus:border-[#FF5722] outline-none px-4 py-2.5 text-sm rounded-sm transition-colors"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Password</label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#0B0C10] border border-white/15 focus:border-[#FF5722] outline-none px-4 py-2.5 text-sm rounded-sm transition-colors"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full bg-[#FF5722] hover:bg-[#F4511E] text-white font-medium py-3 rounded-sm transition-colors active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {busy ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-white/10">
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Sample Accounts</div>
            <div className="space-y-1.5 text-xs text-slate-400">
              <div><span className="text-[#FF5722] mono">ADMIN</span> · dasrajat131@gmail.com / Admin@12345</div>
              <div><span className="text-[#FF5722] mono">MANAGER</span> · manager@sipl.local / Manager@123</div>
              <div><span className="text-[#FF5722] mono">ENGINEER</span> · alebar.khan@sipl.local / Engineer@123</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
